// components/LandingPage.js
import React, { useEffect, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";

// LandingPage component serves as the main entry point for unauthenticated users
const LandingPage = () => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Backend endpoint to handle external authentication
  const AUTH_ENDPOINT = `${process.env.REACT_APP_API_BASE_URL}/auth/external`;

  useEffect(() => {
    // Cancel any existing Google One Tap session on mount
    if (window.google && window.google.accounts && window.google.accounts.id) {
      window.google.accounts.id.cancel();
      console.log("Cancelled previous Google authentication session");
    }

    // Check for "auth_abort" in URL (used when user aborts authentication manually)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("auth_abort") === "true") {
      setError("Authentication was aborted. Please try again.");
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  /**
   * Handles successful Google Login
   * Decodes the JWT token and redirects to backend for further auth processing
   */

  const handleGoogleLogin = (credentialResponse) => {
    setLoading(true);
    setError("");

    console.log("Google Login Success:", credentialResponse);
    const idToken = credentialResponse.credential;

    try {
      localStorage.setItem("idToken", idToken);
      // Decode the ID token to extract user information
      const decodedToken = jwtDecode(idToken);
      console.log("Decoded token:", decodedToken);

      const email = decodedToken.email;

      if (!email) {
        throw new Error("Email not found in token");
      }

      // Prepare user data object
      const userData = {
        email: decodedToken.email,
        name: decodedToken.name || "",
        givenName: decodedToken.given_name || "",
        familyName: decodedToken.family_name || "",
        picture: decodedToken.picture || "",
      };

      // Save user info locally for further use
      localStorage.setItem("userInfo", JSON.stringify(userData));
      console.log("Stored user data:", userData);

      // Redirect to backend to exchange ID token for access token
      //window.location.href = `${AUTH_ENDPOINT}?token=${encodeURIComponent(idToken)}&email=${encodeURIComponent(email)}&provider=google`;
      window.location.href = `${AUTH_ENDPOINT}?token=${encodeURIComponent(idToken)}&email=${encodeURIComponent(email)}&provider=google&client_id=google-auth-demo`;
    } catch (error) {
      console.error("Error processing token:", error);
      setError("Authentication failed. Please try again.");
      setLoading(false);
    }
  };

  /**
   * Handles Google login failure
   */

  const handleGoogleError = () => {
    console.error("Google login failed");
    setError("Google authentication failed. Please try again.");
    setLoading(false);
  };

  return (
    <div className="landing-page">
      <header className="landing-header">
        <div className="logo">Demo App</div>
        <nav className="nav-links">
          <a href="#features">Features</a>
          <a href="#pricing">Pricing</a>
          <a href="#about">About</a>
        </nav>
      </header>

      <section className="hero">
        <div className="hero-content">
          <h1>Welcome to Our Amazing Platform</h1>
          <p>
            The one-stop solution for all your needs. Sign in to get started.
          </p>

          {error && <div className="error-message">{error}</div>}

          {/* Google login button or loading indicator */}
          <div className="auth-container">
            {loading ? (
              <div className="loading-button">Authenticating...</div>
            ) : (
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={handleGoogleError}
                useOneTap
                flow="auth-code"
                text="signin_with"
                shape="rectangular"
                size="large"
                logo_alignment="center"
                width="280px"
              />
            )}
          </div>
        </div>
      </section>

      <section id="features" className="features">
        <h2>Key Features</h2>
        <div className="feature-grid">
          <div className="feature-card">
            <div className="feature-icon">🚀</div>
            <h3>Fast & Reliable</h3>
            <p>Lightning-fast performance you can count on</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🔒</div>
            <h3>Secure Access</h3>
            <p>Your data is protected with enterprise-grade security</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📊</div>
            <h3>Advanced Analytics</h3>
            <p>Gain insights with our powerful analytics tools</p>
          </div>
        </div>
      </section>

      <footer className="footer">
        <p>&copy; 2025 Demo Application. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default LandingPage;
